'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

type RecordStatus = 'Archived' | 'Pending Review' | 'Processing' | 'Flagged' | 'Uploaded';
type RecordCategory = 'Lodge Records' | 'Physical Artifacts' | 'Regalia & Textiles' | 'Member History' | 'Building & Property' | 'Charters & Bylaws';

interface ArchiveRecord {
  id: string;
  title: string;
  category: RecordCategory;
  historicalDate: string;
  decade: string;
  lodge: string;
  status: RecordStatus;
  transcriptionStatus: 'Complete' | 'In Progress' | 'Not Started' | 'Corrected';
  uploader: string;
  fileType: string;
  pageCount?: number;
  aiConfidence?: number;
  uploadedAt: string;
}

const mockRecords: ArchiveRecord[] = [
  { id: 'rec-001', title: 'Charter of Constitution — St. Andrew\'s Lodge', category: 'Charters & Bylaws', historicalDate: '12 June 1769', decade: '1760s', lodge: 'St. Andrew\'s No. 1', status: 'Archived', transcriptionStatus: 'Corrected', uploader: 'R. Sinclair', fileType: 'PDF', pageCount: 4, aiConfidence: 91, uploadedAt: '15 Jan 2026' },
  { id: 'rec-002', title: 'Meeting Minutes — Regular Communication', category: 'Lodge Records', historicalDate: '07 March 1887', decade: '1880s', lodge: 'St. Andrew\'s No. 1', status: 'Archived', transcriptionStatus: 'Complete', uploader: 'R. Sinclair', fileType: 'JPG', pageCount: 2, aiConfidence: 85, uploadedAt: '20 Jan 2026' },
  { id: 'rec-003', title: 'Past Master\'s Apron — Embroidered Lambskin', category: 'Regalia & Textiles', historicalDate: 'circa 1830', decade: '1830s', lodge: 'St. Andrew\'s No. 1', status: 'Archived', transcriptionStatus: 'Not Started', uploader: 'H. MacLeod', fileType: 'JPG', uploadedAt: '22 Jan 2026' },
  { id: 'rec-004', title: 'Initiation Record — William A. MacPherson', category: 'Member History', historicalDate: '03 October 1903', decade: '1900s', lodge: 'St. Andrew\'s No. 1', status: 'Pending Review', transcriptionStatus: 'In Progress', uploader: 'R. Sinclair', fileType: 'PDF', pageCount: 1, aiConfidence: 79, uploadedAt: '28 Apr 2026' },
  { id: 'rec-005', title: 'Treasurer\'s Report — Annual Statement', category: 'Lodge Records', historicalDate: 'March 1892', decade: '1890s', lodge: 'St. Andrew\'s No. 1', status: 'Flagged', transcriptionStatus: 'In Progress', uploader: 'R. Sinclair', fileType: 'PDF', pageCount: 3, aiConfidence: 61, uploadedAt: '01 May 2026' },
  { id: 'rec-006', title: 'Altar Bible — Presented to Lodge 1812', category: 'Physical Artifacts', historicalDate: '1812', decade: '1810s', lodge: 'St. Andrew\'s No. 1', status: 'Archived', transcriptionStatus: 'Not Started', uploader: 'H. MacLeod', fileType: 'JPG', uploadedAt: '10 Feb 2026' },
  { id: 'rec-007', title: 'Lodge Hall Cornerstone Photograph', category: 'Building & Property', historicalDate: '14 September 1878', decade: '1870s', lodge: 'St. Andrew\'s No. 1', status: 'Pending Review', transcriptionStatus: 'Complete', uploader: 'H. MacLeod', fileType: 'JPG', aiConfidence: 88, uploadedAt: '29 Apr 2026' },
  { id: 'rec-008', title: 'Bylaws — Revised Edition', category: 'Charters & Bylaws', historicalDate: '1921', decade: '1920s', lodge: 'St. Andrew\'s No. 1', status: 'Archived', transcriptionStatus: 'Corrected', uploader: 'R. Sinclair', fileType: 'PDF', pageCount: 12, aiConfidence: 94, uploadedAt: '05 Mar 2026' },
  { id: 'rec-009', title: 'Meeting Minutes — Emergent Communication', category: 'Lodge Records', historicalDate: '14 April 1887', decade: '1880s', lodge: 'St. Andrew\'s No. 1', status: 'Pending Review', transcriptionStatus: 'In Progress', uploader: 'Contributor Token', fileType: 'JPG', pageCount: 1, aiConfidence: 72, uploadedAt: '02 May 2026' },
  { id: 'rec-010', title: 'Senior Warden\'s Jewel — Cast Silver', category: 'Regalia & Textiles', historicalDate: 'circa 1855', decade: '1850s', lodge: 'St. Andrew\'s No. 1', status: 'Archived', transcriptionStatus: 'Not Started', uploader: 'H. MacLeod', fileType: 'JPG', uploadedAt: '18 Feb 2026' },
  { id: 'rec-011', title: 'Grand Lodge Communication — Annual Returns 1899', category: 'Lodge Records', historicalDate: '1899', decade: '1890s', lodge: 'St. Andrew\'s No. 1', status: 'Processing', transcriptionStatus: 'In Progress', uploader: 'R. Sinclair', fileType: 'PDF', pageCount: 6, aiConfidence: 83, uploadedAt: '03 May 2026' },
  { id: 'rec-012', title: 'Photograph — Lodge Officers 1925', category: 'Member History', historicalDate: '1925', decade: '1920s', lodge: 'St. Andrew\'s No. 1', status: 'Uploaded', transcriptionStatus: 'Not Started', uploader: 'Contributor Token', fileType: 'JPG', uploadedAt: '04 May 2026' },
];

const statusColors: Record<RecordStatus, string> = {
  'Archived': 'status-archived',
  'Pending Review': 'status-pending',
  'Processing': 'status-processing',
  'Flagged': 'status-uploaded',
  'Uploaded': 'status-uploaded',
};

const transcriptionColors: Record<string, { bg: string; color: string }> = {
  'Complete': { bg: 'var(--success-bg)', color: 'var(--success)' },
  'Corrected': { bg: 'rgba(201, 168, 76, 0.15)', color: '#7A5C1E' },
  'In Progress': { bg: 'var(--info-bg)', color: 'var(--info)' },
  'Not Started': { bg: 'var(--muted)', color: 'var(--muted-foreground)' },
};

const categories: RecordCategory[] = ['Lodge Records', 'Physical Artifacts', 'Regalia & Textiles', 'Member History', 'Building & Property', 'Charters & Bylaws'];
const decades = ['1760s', '1810s', '1830s', '1850s', '1870s', '1880s', '1890s', '1900s', '1920s'];
const statuses: RecordStatus[] = ['Archived', 'Pending Review', 'Processing', 'Flagged', 'Uploaded'];

export default function ArchiveCatalog() {
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDecade, setFilterDecade] = useState('');
  const [sortCol, setSortCol] = useState<keyof ArchiveRecord>('uploadedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [hiddenCols, setHiddenCols] = useState<string[]>([]);
  const [showColMenu, setShowColMenu] = useState(false);

  const allCols = [
    { key: 'title', label: 'Record Title' },
    { key: 'category', label: 'Category' },
    { key: 'historicalDate', label: 'Historical Date' },
    { key: 'decade', label: 'Decade' },
    { key: 'status', label: 'Status' },
    { key: 'transcriptionStatus', label: 'Transcription' },
    { key: 'uploader', label: 'Uploaded By' },
    { key: 'fileType', label: 'Format' },
    { key: 'uploadedAt', label: 'Added to Archive' },
  ];

  const visibleCols = allCols.filter((c) => !hiddenCols.includes(c.key));

  const toggleSort = (col: keyof ArchiveRecord) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    return mockRecords.filter((r) => {
      const q = search.toLowerCase();
      const matchSearch = !q || r.title.toLowerCase().includes(q) || r.uploader.toLowerCase().includes(q) || r.historicalDate.toLowerCase().includes(q);
      const matchCat = !filterCategory || r.category === filterCategory;
      const matchStatus = !filterStatus || r.status === filterStatus;
      const matchDecade = !filterDecade || r.decade === filterDecade;
      return matchSearch && matchCat && matchStatus && matchDecade;
    });
  }, [search, filterCategory, filterStatus, filterDecade]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortCol] ?? '';
      const bv = b[sortCol] ?? '';
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [filtered, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSelect = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const toggleAll = () =>
    setSelected(selected.length === paged.length ? [] : paged.map((r) => r.id));

  const handleBulkDelete = () => {
    toast.success(`${selected.length} records removed from queue`);
    setSelected([]);
  };

  const handleBulkArchive = () => {
    toast.success(`${selected.length} records marked as Archived`);
    setSelected([]);
  };

  return (
    <div className="space-y-4">
      {/* Filters bar */}
      <div className="masonic-card p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-52">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: 'var(--muted-foreground)' } as React.CSSProperties}
            />
            <input
              className="masonic-input pl-9 py-2 text-sm"
              placeholder="Search records, uploaders, dates…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          <select
            className="masonic-select text-sm py-2 min-h-0 h-10 w-auto"
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => <option key={`cat-${c}`} value={c}>{c}</option>)}
          </select>

          <select
            className="masonic-select text-sm py-2 min-h-0 h-10 w-auto"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            {statuses.map((s) => <option key={`st-${s}`} value={s}>{s}</option>)}
          </select>

          <select
            className="masonic-select text-sm py-2 min-h-0 h-10 w-auto"
            value={filterDecade}
            onChange={(e) => { setFilterDecade(e.target.value); setPage(1); }}
          >
            <option value="">All Decades</option>
            {decades.map((d) => <option key={`dec-${d}`} value={d}>{d}</option>)}
          </select>

          {(search || filterCategory || filterStatus || filterDecade) && (
            <button
              className="masonic-btn-outline text-sm h-10 min-h-0 px-3"
              onClick={() => { setSearch(''); setFilterCategory(''); setFilterStatus(''); setFilterDecade(''); setPage(1); }}
            >
              <Icon name="XMarkIcon" size={15} />
              Clear
            </button>
          )}

          <div className="ml-auto flex gap-2">
            <div className="relative">
              <button
                className="masonic-btn-outline text-sm h-10 min-h-0 px-3"
                onClick={() => setShowColMenu(!showColMenu)}
              >
                <Icon name="ViewColumnsIcon" size={15} />
                Columns
              </button>
              {showColMenu && (
                <div
                  className="absolute right-0 top-full mt-1 z-20 masonic-card p-3 w-52 shadow-masonic-md"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    Toggle Columns
                  </p>
                  {allCols.map((col) => (
                    <label key={`col-${col.key}`} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={!hiddenCols.includes(col.key)}
                        onChange={() =>
                          setHiddenCols((h) =>
                            h.includes(col.key) ? h.filter((x) => x !== col.key) : [...h, col.key]
                          )
                        }
                        style={{ accentColor: 'var(--accent)' }}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <Link href="/document-capture-screen" className="masonic-btn-primary text-sm h-10 min-h-0 px-4">
              <Icon name="PlusIcon" size={15} />
              Add Record
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''} found
          </span>
          {selected.length > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium animate-fade-in"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              <span>{selected.length} selected</span>
              <button
                onClick={handleBulkArchive}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs hover:bg-white/10 transition-colors"
              >
                <Icon name="ArchiveBoxIcon" size={13} />
                Archive
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1 px-2 py-0.5 rounded text-xs hover:bg-white/10 transition-colors"
              >
                <Icon name="TrashIcon" size={13} />
                Remove
              </button>
              <button onClick={() => setSelected([])} className="hover:opacity-70">
                <Icon name="XMarkIcon" size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="masonic-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--muted)', borderBottom: '2px solid var(--border)' }}>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={paged.length > 0 && selected.length === paged.length}
                    onChange={toggleAll}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                </th>
                {visibleCols.map((col) => (
                  <th
                    key={`th-${col.key}`}
                    className="text-left px-3 py-3 font-semibold uppercase tracking-wider text-xs cursor-pointer select-none whitespace-nowrap hover:bg-secondary transition-colors"
                    style={{ color: sortCol === col.key ? 'var(--accent)' : 'var(--muted-foreground)' }}
                    onClick={() => toggleSort(col.key as keyof ArchiveRecord)}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {sortCol === col.key && (
                        <Icon
                          name={sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                          size={12}
                        />
                      )}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={visibleCols.length + 2} className="py-16 text-center">
                    <Icon
                      name="ArchiveBoxXMarkIcon"
                      size={40}
                      style={{ color: 'var(--muted-foreground)', margin: '0 auto 12px' } as React.CSSProperties}
                    />
                    <p className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                      No records found
                    </p>
                    <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                      Try adjusting your search or filters, or add a new record to the archive.
                    </p>
                    <Link href="/document-capture-screen" className="masonic-btn-primary text-sm mt-4 inline-flex">
                      <Icon name="PlusIcon" size={15} />
                      Capture New Record
                    </Link>
                  </td>
                </tr>
              ) : (
                paged.map((rec, idx) => (
                  <tr
                    key={rec.id}
                    className="transition-colors hover:bg-muted/40 group"
                    style={{
                      borderBottom: idx < paged.length - 1 ? '1px solid var(--border)' : 'none',
                      background: selected.includes(rec.id) ? 'rgba(201, 168, 76, 0.06)' : undefined,
                    }}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(rec.id)}
                        onChange={() => toggleSelect(rec.id)}
                        style={{ accentColor: 'var(--accent)' }}
                      />
                    </td>

                    {!hiddenCols.includes('title') && (
                      <td className="px-3 py-3 max-w-xs">
                        <div className="flex items-start gap-2">
                          <div>
                            <p className="font-medium leading-snug" style={{ color: 'var(--foreground)' }}>
                              {rec.title}
                            </p>
                            {rec.pageCount && (
                              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                                {rec.pageCount} page{rec.pageCount > 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                    )}

                    {!hiddenCols.includes('category') && (
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span
                          className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{ background: 'var(--info-bg)', color: 'var(--info)' }}
                        >
                          {rec.category}
                        </span>
                      </td>
                    )}

                    {!hiddenCols.includes('historicalDate') && (
                      <td className="px-3 py-3 text-sm whitespace-nowrap tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
                        {rec.historicalDate}
                      </td>
                    )}

                    {!hiddenCols.includes('decade') && (
                      <td className="px-3 py-3 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                        {rec.decade}
                      </td>
                    )}

                    {!hiddenCols.includes('status') && (
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`status-badge ${statusColors[rec.status]}`}>
                          {rec.status}
                        </span>
                      </td>
                    )}

                    {!hiddenCols.includes('transcriptionStatus') && (
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span
                          className="status-badge"
                          style={{
                            background: transcriptionColors[rec.transcriptionStatus].bg,
                            color: transcriptionColors[rec.transcriptionStatus].color,
                          }}
                        >
                          {rec.transcriptionStatus}
                        </span>
                      </td>
                    )}

                    {!hiddenCols.includes('uploader') && (
                      <td className="px-3 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--muted-foreground)' }}>
                        {rec.uploader}
                      </td>
                    )}

                    {!hiddenCols.includes('fileType') && (
                      <td className="px-3 py-3">
                        <span
                          className="text-xs font-mono px-2 py-0.5 rounded font-semibold"
                          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                        >
                          {rec.fileType}
                        </span>
                      </td>
                    )}

                    {!hiddenCols.includes('uploadedAt') && (
                      <td className="px-3 py-3 text-sm whitespace-nowrap tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
                        {rec.uploadedAt}
                      </td>
                    )}

                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link
                          href="/document-transcription-review"
                          className="p-1.5 rounded hover:bg-muted transition-colors"
                          title="Review transcription"
                        >
                          <Icon name="DocumentMagnifyingGlassIcon" size={15} style={{ color: 'var(--primary)' } as React.CSSProperties} />
                        </Link>
                        <button
                          className="p-1.5 rounded hover:bg-muted transition-colors"
                          title="Download original file"
                          onClick={() => toast.success(`Downloading ${rec.title}`)}
                        >
                          <Icon name="ArrowDownTrayIcon" size={15} style={{ color: 'var(--muted-foreground)' } as React.CSSProperties} />
                        </button>
                        <button
                          className="p-1.5 rounded hover:bg-muted transition-colors"
                          title="Flag for re-scan"
                          onClick={() => toast.warning(`${rec.title} flagged for re-scan`)}
                        >
                          <Icon name="FlagIcon" size={15} style={{ color: 'var(--warning)' } as React.CSSProperties} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-wrap gap-3"
          style={{ borderTop: '1px solid var(--border)', background: 'var(--muted)' }}
        >
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
            <span>Show</span>
            <select
              className="masonic-select text-sm py-1 min-h-0 h-8 w-16"
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            >
              {[5, 10, 20, 50].map((n) => (
                <option key={`pp-${n}`} value={n}>{n}</option>
              ))}
            </select>
            <span>of {filtered.length} records</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage(1)}
              title="First page"
            >
              <Icon name="ChevronDoubleLeftIcon" size={14} />
            </button>
            <button
              className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              title="Previous page"
            >
              <Icon name="ChevronLeftIcon" size={14} />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = i + 1;
              return (
                <button
                  key={`page-${p}`}
                  onClick={() => setPage(p)}
                  className="w-8 h-8 rounded text-sm font-medium transition-colors"
                  style={{
                    background: page === p ? 'var(--primary)' : 'transparent',
                    color: page === p ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                  }}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              title="Next page"
            >
              <Icon name="ChevronRightIcon" size={14} />
            </button>
            <button
              className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40"
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
              title="Last page"
            >
              <Icon name="ChevronDoubleRightIcon" size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}